import { usePostHog } from "posthog-js/react";
import { useMemo } from "react";

import type { DeepLink } from "@/helpers/deep-link";
import { buildDeepLinkUrl } from "@/helpers/deep-link";
import { canCreateAnnouncementMessage, canCreateStandardMessage } from "@/helpers/permissions";
import { useProject } from "@/hooks/Network/useProject";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { useTranslation } from "@/translations";

import type { ContextMenuItem } from "../ContextMenu/ContextMenu";
import { useFlashToast } from "../FlashToast/FlashToast";
import { File02Icon } from "../Icons/File02Icon";
import { Link03Icon } from "../Icons/Link03Icon";
import { MessageChatCircleIcon } from "../Icons/MessageChatCircleIcon";

export const useShareEntityMenuItems = ({
  deepLinkPayload,
  callbacks,
}: {
  deepLinkPayload: Omit<DeepLink["payload"], "projectId">;
  callbacks: {
    shareToCommunityFeed: () => void;
    shareToChat: () => void;
  };
}): ContextMenuItem[] => {
  const { t } = useTranslation();
  const project = useProject();
  const sessionUser = useSessionUser();
  const showFlashToast = useFlashToast();
  const postHog = usePostHog();
  const hasPermission = usePermission();
  const { appBrandingConfig } = useProjectAppBrandingConfig();

  const { entityId, entityType, folderId, groupTypeId } = deepLinkPayload;

  const deepLink = useMemo(() => {
    if (entityType === "group") {
      return buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          groupTypeId: groupTypeId || "",
        },
      });
    } else if (entityType === "document") {
      return buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          folderId: folderId || "",
        },
      });
    } else {
      return buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
        },
      });
    }
  }, [appBrandingConfig.deepLink, entityId, entityType, folderId, groupTypeId, project.id]);

  const menuItems = useMemo(() => {
    if (!deepLink) {
      return [];
    }

    const currMenuItems: ContextMenuItem[] = [
      {
        labels: {
          default: t("component.share-btn.menu-item.share-via-chat.title"),
        },
        icon: <MessageChatCircleIcon />,
        value: "share-via-chat",
        callbackFn: () => {
          postHog.capture("clicked_share_entity_share_via_chat", {
            entity_type: entityType,
          });

          callbacks.shareToChat();
        },
        "data-testid": "share-btn-menu-share-via-chat-btn",
      },
    ];

    const canShareToCommunityFeed = sessionUser.isAdmin
      ? hasPermission(canCreateAnnouncementMessage) || hasPermission(canCreateStandardMessage)
      : hasPermission(canCreateStandardMessage);
    if (canShareToCommunityFeed) {
      currMenuItems.push({
        labels: {
          default: t("component.share-btn.menu-item.share-to-community-feed.title"),
        },
        icon: <File02Icon />,
        value: "share-to-community-feed",
        callbackFn: () => {
          postHog.capture("clicked_share_entity_share_to_community_feed", {
            entity_type: entityType,
          });

          callbacks.shareToCommunityFeed();
        },
        "data-testid": "share-btn-menu-share-to-community-feed-btn",
      });
    }

    currMenuItems.push({
      labels: {
        default: t("component.share-btn.menu-item.copy-link.title"),
      },
      icon: <Link03Icon />,
      value: "copy-link",
      callbackFn: async () => {
        postHog.capture("clicked_share_entity_copy_link", {
          entity_type: entityType,
        });

        try {
          await navigator.clipboard.writeText(deepLink);

          showFlashToast({
            variant: "success",
            message: t("component.share-btn.notification.link-copied.title"),
          });
        } catch (e) {
          console.error("Error copying deeplink to clipboard:", e);
          showFlashToast({
            variant: "warning",
            message: t("component.share-btn.notification.link-copy-failed.title"),
          });
        }
      },
      "data-testid": "share-btn-menu-copy-link-btn",
    });

    return currMenuItems;
  }, [deepLink, hasPermission, t, postHog, entityType, callbacks, sessionUser.isAdmin, showFlashToast]);

  return menuItems;
};
